import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback
} from 'react'
import axios from 'axios'
import { useSocket } from './useSocket' // 新增的 hook
import { API_BASE_URL } from '../utils/apiutils'
import {
  getBusinessScopeToken,
  setBusinessScopeToken,
  setLogoutHandler
} from '../utils/api'
import { setBusinessTimezone, DEFAULT_TIMEZONE } from '../utils/timezone'

const AuthContext = createContext(null)

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null)
  const [businessId, setBusinessId] = useState(null)
  const [permissions, setPermissions] = useState([])
  const [businessRole, setBusinessRole] = useState(null)
  const [businesses, setBusinesses] = useState([])
  const [accountStatus, setAccountStatus] = useState(null)
  const [permissionsLoaded, setPermissionsLoaded] = useState(false)
  const [businessPlan, setBusinessPlan] = useState(null) // 會員方案
  // 首次身分驗證是否還在進行中。
  // PrivateRoute 靠這個旗標等結果，不必自己再打一次 checkBusinessStatus。
  const [authInitializing, setAuthInitializing] = useState(true)
  const { connectSocket, disconnectSocket } = useSocket()
  const getScopedAuthHeaders = useCallback(() => {
    const scopedToken = getBusinessScopeToken()
    return scopedToken
      ? { Authorization: `Bearer ${scopedToken}` }
      : undefined
  }, [])

  const handleSocketConnection = useCallback(
    (userData) => {
      if (userData && userData.businessId) {
        connectSocket(userData.businessId)
      } else {
        disconnectSocket()
      }
    },
    [connectSocket, disconnectSocket]
  )

  // 獲取商家會員方案
  const fetchBusinessPlan = useCallback(async (businessId) => {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/api/business_B/${businessId}`,
        {
          withCredentials: true,
          headers: getScopedAuthHeaders()
        }
      )
      if (response.data?.subscription?.plan) {
        setBusinessPlan(response.data.subscription.plan)
      } else {
        setBusinessPlan('free') // 默認為免費方案
      }
    } catch (error) {
      console.error('獲取商家會員方案時發生錯誤:', error)
      setBusinessPlan('free') // 默認為免費方案
    }
  }, [getScopedAuthHeaders])

  const fetchUserPermissions = useCallback(async (userId, businessId) => {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/api/businesses/${businessId}/employees/${userId}/role`,
        {
          withCredentials: true,
          headers: getScopedAuthHeaders()
        }
      )

      if (response.data.success) {
        setPermissions(response.data.data.permissions || [])
        // businessRole 已經從登入響應獲取，這裡不再覆蓋
        setAccountStatus(response.data.data.accountStatus || 'active')
        setPermissionsLoaded(true)
      }
    } catch (error) {
      console.error('獲取使用者許可權時發生錯誤:', error)
      // 即使失敗也標記為已載入，避免無限 loading
      setPermissions([])
      // businessRole 保持從登入響應獲取的值，不重置為 null
      setAccountStatus(null)
      setPermissionsLoaded(true)
    }
  }, [getScopedAuthHeaders])

  const checkBusinessStatus = useCallback(async () => {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/api/auth/check-business-status`,
        {
          withCredentials: true,
          headers: getScopedAuthHeaders()
        }
      )

      if (response.data.success) {
        // 全站的「現在幾點／今天是哪天」都以店家時區為準，不看瀏覽器所在時區。
        // 店家出國時後台仍顯示台灣時間，靠的就是這一行。
        setBusinessTimezone(response.data.timezone)
        setUser(response.data)
        setBusinessId(response.data.businessId)
        setBusinessRole(response.data.businessRole) // 直接從登入響應獲取
        setBusinesses(response.data.businesses || [])
        // check-business-status 主要用於讀狀態，通常不會回傳新 token；避免把既有 scope token 清掉
        if (response.data.businessAccessToken) {
          setBusinessScopeToken(response.data.businessAccessToken)
        }
        handleSocketConnection(response.data)

        // 許可權與會員方案互不相依，平行發出。
        // 原本兩個 await 串起來，等於白等一整支 API 的時間（實測約 +2.7s）。
        const userId = response.data.userId || response.data._id
        await Promise.all([
          userId && response.data.businessId
            ? fetchUserPermissions(userId, response.data.businessId)
            : Promise.resolve(),
          response.data.businessId
            ? fetchBusinessPlan(response.data.businessId)
            : Promise.resolve()
        ])
      } else {
        setUser(null)
        setBusinessId(null)
        setPermissions([])
        setBusinessRole(null)
        setBusinesses([])
        setAccountStatus(null)
        setBusinessPlan(null)
        setPermissionsLoaded(true)
        setBusinessScopeToken(null)
        handleSocketConnection(null)
      }
    } catch (error) {
      console.error('檢查使用者狀態時發生錯誤:', error)
      setUser(null)
      setPermissions([])
      setBusinessRole(null)
      setBusinesses([])
      setAccountStatus(null)
      setBusinessPlan(null)
      setPermissionsLoaded(true)
      setBusinessScopeToken(null)
      handleSocketConnection(null)
    }
  }, [
    handleSocketConnection,
    fetchUserPermissions,
    fetchBusinessPlan,
    getScopedAuthHeaders
  ])

  const checkAdminStatus = useCallback(async () => {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/api/auth/check-admin-status`,
        { withCredentials: true }
      )
      if (response.data.success) {
        const platformAdminTier =
          response.data.platformAdminTier === 'staff' ? 'staff' : 'super'
        setUser({
          avatar: response.data.avatar,
          email: response.data.email,
          role: response.data.role,
          userId: response.data.userId,
          username: response.data.username,
          platformAdminTier
        })
        return { success: true, platformAdminTier }
      }
      setUser(null)
      return { success: false }
    } catch (error) {
      console.error('檢查管理員狀態時發生錯誤:', error)
      setUser(null)
      return { success: false }
    }
  }, [])

  useEffect(() => {
    const initializeAuth = async () => {
      try {
        const path = window.location.pathname
        if (path.startsWith('/admin') || path === '/register') {
          await checkAdminStatus()
        } else {
          await checkBusinessStatus()
        }
      } finally {
        setAuthInitializing(false)
      }
    }

    initializeAuth()
  }, [checkBusinessStatus, checkAdminStatus])

  const login = useCallback(
    async (credentials) => {
      try {
        const response = await axios.post(
          `${API_BASE_URL}/api/auth/login/business`,
          credentials,
          { withCredentials: true }
        )

        setBusinessTimezone(response.data.timezone)
        setUser(response.data)
        setBusinessId(response.data.businessId)
        setBusinessRole(response.data.businessRole) // 直接從登入響應獲取
        setBusinesses(response.data.businesses || [])
        setBusinessScopeToken(response.data.businessAccessToken || null)
        handleSocketConnection(response.data)

        // 許可權與會員方案互不相依，平行發出。
        // 原本兩個 await 串起來，等於白等一整支 API 的時間（實測約 +2.7s）。
        const userId = response.data.userId || response.data._id
        await Promise.all([
          userId && response.data.businessId
            ? fetchUserPermissions(userId, response.data.businessId)
            : Promise.resolve(),
          response.data.businessId
            ? fetchBusinessPlan(response.data.businessId)
            : Promise.resolve()
        ])

        return response.data
      } catch (error) {
        throw error
      }
    },
    [handleSocketConnection, fetchUserPermissions, fetchBusinessPlan]
  )

  const logout = useCallback(async () => {
    try {
      await axios.post(
        `${API_BASE_URL}/api/auth/logout`,
        {},
        { withCredentials: true }
      )
    } catch (e) {
      /* 登出 API 失敗仍清除前端狀態 */
    }
    setUser(null)
    setBusinessId(null)
    setPermissions([])
    setBusinessRole(null)
    setBusinesses([])
    setAccountStatus(null)
    setBusinessPlan(null)
    setPermissionsLoaded(false)
    setBusinessScopeToken(null)
    setBusinessTimezone(DEFAULT_TIMEZONE)
    handleSocketConnection(null)
  }, [handleSocketConnection])

  const switchBusiness = useCallback(
    async (targetBusinessId) => {
      if (!targetBusinessId) return
      const response = await axios.post(
        `${API_BASE_URL}/api/auth/switch-business`,
        { businessId: targetBusinessId },
        {
          withCredentials: true,
          headers: getScopedAuthHeaders()
        }
      )

      if (!response.data?.success) {
        throw new Error(response.data?.error || '切換店家失敗')
      }

      setBusinessScopeToken(response.data.businessAccessToken || null)
      await checkBusinessStatus()
      return response.data
    },
    [checkBusinessStatus, getScopedAuthHeaders]
  )

  useEffect(() => {
    setLogoutHandler(() => logout())
    return () => setLogoutHandler(null)
  }, [logout])

  const refreshToken = useCallback(async () => {
    try {
      await axios.post(
        `${API_BASE_URL}/api/auth/refresh-token`,
        {},
        { withCredentials: true }
      )
      await checkBusinessStatus()
    } catch (error) {
      setUser(null)
      setBusinessId(null)
      setPermissions([])
      setBusinessRole(null)
      setBusinesses([])
      setAccountStatus(null)
      setBusinessPlan(null)
      setPermissionsLoaded(true)
      setBusinessScopeToken(null)
      handleSocketConnection(null)
    }
  }, [checkBusinessStatus, handleSocketConnection])

  const contextValue = {
    user,
    businessId,
    permissions,
    businessRole,
    businesses,
    accountStatus,
    permissionsLoaded,
    businessPlan, // 會員方案
    authInitializing, // 首次身分驗證進行中
    login,
    logout,
    checkBusinessStatus,
    refreshToken,
    switchBusiness,
    checkAdminStatus,
    fetchUserPermissions,
    fetchBusinessPlan // 獲取會員方案的方法
  }

  return (
    <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>
  )
}

export const useAuth = () => useContext(AuthContext)
